/* AdminCommissions — internal commission ledger + manual payout
   workflow. Lists rows from `/api/admin/commissions`, surfaces totals
   per Funeral Home and Regional Partner, and lets the platform admin
   approve / mark-paid / cancel each row.

   Auto-Stripe-Connect transfers are OFF by default (manual mode). The
   page shows a banner if the feature flag has been re-enabled. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError, BACKEND_URL } from '../lib/api';
import { toast } from 'sonner';
import { Download, CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw } from 'lucide-react';

const STATUS_TABS = [
  { id: 'all',                label: 'All' },
  { id: 'pending',            label: 'Pending' },
  { id: 'approved',           label: 'Approved' },
  { id: 'paid',               label: 'Paid' },
  { id: 'cancelled',          label: 'Cancelled' },
  { id: 'needs_admin_review', label: 'Needs Review' },
];

const STATUS_PILL = {
  pending:            'bg-amber-50 text-amber-700 border border-amber-200',
  approved:           'bg-sky-50 text-sky-700 border border-sky-200',
  paid:               'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancelled:          'bg-slate-100 text-slate-600 border border-slate-200',
  needs_admin_review: 'bg-red-50 text-red-700 border border-red-200',
  reversed:           'bg-purple-50 text-purple-700 border border-purple-200',
  failed:             'bg-red-50 text-red-700 border border-red-200',
};

function money(cents) {
  return `$${((Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso.slice(0, 10); }
}

export default function AdminCommissions() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoEnabled, setAutoEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = tab === 'all' ? '' : tab;
      const { data } = await api.get(`/admin/commissions?limit=200${status ? `&status=${status}` : ''}`);
      setRows(data.rows || []);
      setAutoEnabled(!!data.auto_payouts_enabled);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [tab]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/commissions/summary');
      setSummary(data);
    } catch (e) {
      console.warn('summary load failed:', e);
    }
  }, []);

  useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);

  const totalOwedFH = useMemo(() => {
    if (!summary) return 0;
    return summary.funeral_homes?.reduce((acc, p) => acc + (p.owed_cents || 0), 0) || 0;
  }, [summary]);

  const totalOwedRP = useMemo(() => {
    if (!summary) return 0;
    return summary.regional_partners?.reduce((acc, p) => acc + (p.owed_cents || 0), 0) || 0;
  }, [summary]);

  const refreshAll = () => { load(); loadSummary(); };

  return (
    <>
      <Navbar />
      <AdminLayout>
        <div className="ml-container py-8 space-y-6" data-testid="admin-commissions">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="ml-overline">Admin</div>
              <h1 className="font-serif text-3xl text-[hsl(var(--primary))] mt-1">
                Commission Payouts
              </h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
                Internal commission ledger for partner sales of the $199 Memorial and $299
                Forever Here packages. $100 to the Funeral Home, $25 override to the Regional
                Partner — all manually approved and paid until auto-payouts is enabled.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={refreshAll} className="ml-btn-outline !py-2 text-xs" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <a
                href={`${BACKEND_URL}/api/admin/commissions/export.csv${tab && tab !== 'all' ? `?status=${tab}` : ''}`}
                className="ml-btn-gold !py-2 text-xs"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" /> Export CSV
              </a>
            </div>
          </header>

          {autoEnabled ? (
            <div className="ml-card border-amber-300/70 bg-amber-50/40">
              <div className="text-sm font-semibold text-amber-800">
                Auto-payouts mode is ENABLED
              </div>
              <div className="text-xs text-amber-700 mt-1">
                Commissions are being sent directly through Stripe Connect. Set
                <code className="px-1 mx-1 rounded bg-amber-100">AUTO_PAYOUTS_ENABLED=false</code>
                in the deploy env to switch back to manual mode.
              </div>
            </div>
          ) : (
            <div className="ml-card border-emerald-300/60 bg-emerald-50/30">
              <div className="text-sm font-semibold text-emerald-800">
                Manual-payout mode (default for launch)
              </div>
              <div className="text-xs text-emerald-700 mt-1">
                Every commission row stays internal until you approve and mark it paid.
                No Stripe Connect transfers are being created automatically.
              </div>
            </div>
          )}

          {/* Summary cards */}
          {summary && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary-cards">
              <SummaryCard label="Owed to Funeral Homes" value={money(totalOwedFH)} sub={`${summary.funeral_homes?.length || 0} partner${summary.funeral_homes?.length === 1 ? '' : 's'}`} tone="emerald" />
              <SummaryCard label="Owed to Regional Partners" value={money(totalOwedRP)} sub={`${summary.regional_partners?.length || 0} partner${summary.regional_partners?.length === 1 ? '' : 's'}`} tone="indigo" />
              <SummaryCard label="Paid to date" value={money(summary.global_cents?.paid_cents || 0)} sub="All approved + marked paid" tone="slate" />
              <SummaryCard label="Needs review" value={money(summary.global_cents?.needs_review_cents || 0)} sub="Refunds on already-paid rows" tone="red" />
            </div>
          )}

          {/* Per-partner totals */}
          {summary && (summary.funeral_homes?.length > 0 || summary.regional_partners?.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-4">
              <PartnerTotalsCard title="Funeral Homes" rows={summary.funeral_homes} testId="fh-totals" />
              <PartnerTotalsCard title="Regional Partners" rows={summary.regional_partners} testId="rp-totals" />
            </div>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 border-b border-[hsl(var(--primary)/0.12)] pb-2">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold transition-all ${
                  tab === t.id
                    ? 'bg-[hsl(var(--primary))] text-white'
                    : 'bg-[hsl(var(--muted))]/40 text-[hsl(var(--primary)/0.7)] hover:bg-[hsl(var(--muted))]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Ledger table */}
          <div className="overflow-x-auto" data-testid="ledger-table">
            {loading ? (
              <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]">Loading commission ledger…</div>
            ) : rows.length === 0 ? (
              <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]">
                No commission rows in this view.
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[hsl(var(--primary)/0.6)] border-b border-[hsl(var(--primary)/0.1)]">
                    <th className="py-2 pr-4">Created</th>
                    <th className="py-2 pr-4">Partner</th>
                    <th className="py-2 pr-4">Kind</th>
                    <th className="py-2 pr-4">Memorial</th>
                    <th className="py-2 pr-4">Package</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <CommissionRow key={r.commission_id} row={r} onChanged={refreshAll} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </AdminLayout>
    </>
  );
}

function SummaryCard({ label, value, sub, tone }) {
  const palette = {
    emerald: 'bg-emerald-50/40 border-emerald-200/60 text-emerald-900',
    indigo:  'bg-indigo-50/40 border-indigo-200/60 text-indigo-900',
    slate:   'bg-slate-50/60 border-slate-200/60 text-slate-900',
    red:     'bg-red-50/40 border-red-200/60 text-red-900',
  }[tone] || 'bg-white border-[hsl(var(--primary)/0.1)]';
  return (
    <div className={`ml-card !p-5 ${palette}`}>
      <div className="ml-overline">{label}</div>
      <div className="mt-2 font-serif text-2xl">{value}</div>
      <div className="text-[11px] uppercase tracking-widest font-semibold opacity-70 mt-1">{sub}</div>
    </div>
  );
}

function PartnerTotalsCard({ title, rows, testId }) {
  return (
    <div className="ml-card" data-testid={testId}>
      <div className="ml-overline">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))] mt-2">No commissions yet.</div>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.slice(0, 10).map((p) => (
            <li key={p.partner_id} className="flex items-center justify-between gap-2 border-b border-[hsl(var(--primary)/0.06)] pb-2 last:border-0">
              <div className="min-w-0">
                <div className="font-medium text-[hsl(var(--primary))] truncate">
                  {p.business_name || p.name || p.partner_id}
                </div>
                <div className="text-[11px] text-[hsl(var(--primary)/0.6)] truncate">{p.email}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-semibold text-[hsl(var(--primary))]">{money(p.owed_cents)}</div>
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary)/0.55)]">owed</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CommissionRow({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const pill = STATUS_PILL[row.status] || 'bg-slate-50 text-slate-700 border border-slate-200';

  const doApprove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/approve`);
      toast.success('Approved — ready for payout');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doMarkPaid = async () => {
    if (busy) return;
    const ref = window.prompt('Payout reference (check #, Zelle, ACH, etc.)');
    if (!ref || !ref.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/mark-paid`, { payout_reference: ref.trim() });
      toast.success('Marked as paid');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (busy) return;
    const reason = window.prompt('Cancellation reason:');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/cancel`, { reason: reason.trim() });
      toast.success('Cancelled');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doResolveReview = async (resolution) => {
    if (busy) return;
    if (!window.confirm(`Resolve as "${resolution}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/resolve-review`, { resolution });
      toast.success('Review resolved');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doRetry = async () => {
    if (busy) return;
    if (!window.confirm('Retry Stripe Connect transfer now?')) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/retry`);
      toast.success('Transfer sent');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doHold = async () => {
    if (busy) return;
    const reason = window.prompt('Hold reason (will be logged):');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/hold`, { reason: reason.trim() });
      toast.success('Payout held');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doRelease = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/commissions/${row.commission_id}/release`);
      toast.success('Hold released');
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <tr className="border-b border-[hsl(var(--primary)/0.05)] hover:bg-[hsl(var(--primary)/0.025)]" data-testid={`row-${row.commission_id}`}>
      <td className="py-3 pr-4 whitespace-nowrap text-xs">{fmtDate(row.created_at)}</td>
      <td className="py-3 pr-4">
        <div className="font-medium text-[hsl(var(--primary))]">
          {row.partner?.business_name || row.partner?.name || row.partner_id}
        </div>
        <div className="text-[11px] text-[hsl(var(--primary)/0.55)]">
          {row.partner?.account_type === 'memorial_partner' ? 'Funeral Home' : 'Regional Partner'}
        </div>
      </td>
      <td className="py-3 pr-4 text-xs uppercase tracking-widest">{row.source_kind}</td>
      <td className="py-3 pr-4 text-xs">{row.memorial?.full_name || '—'}</td>
      <td className="py-3 pr-4 text-xs uppercase tracking-widest">{row.package_id || '—'}</td>
      <td className="py-3 pr-4 text-right font-mono font-semibold">{money(row.amount_cents)}</td>
      <td className="py-3 pr-4">
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full ${pill}`}>
          {row.status}
        </span>
        {row.on_hold && (
          <span className="ml-1 inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
            ON HOLD
          </span>
        )}
        {row.transfer_id && (
          <div className="text-[10px] text-emerald-700 mt-1 font-mono">tr: {row.transfer_id}</div>
        )}
        {row.payout_reference && (
          <div className="text-[10px] text-[hsl(var(--primary)/0.55)] mt-1">Ref: {row.payout_reference}</div>
        )}
        {row.failure_reason && (
          <div className="text-[10px] text-red-700 mt-1 italic">⚠ {row.failure_reason}</div>
        )}
        {row.cancellation_reason && (
          <div className="text-[10px] text-[hsl(var(--primary)/0.55)] mt-1 italic">{row.cancellation_reason}</div>
        )}
        {row.needs_review_reason && (
          <div className="text-[10px] text-red-700 mt-1 italic">{row.needs_review_reason}</div>
        )}
        {row.retry_count > 0 && (
          <div className="text-[10px] text-[hsl(var(--primary)/0.55)] mt-1">retry × {row.retry_count}</div>
        )}
      </td>
      <td className="py-3 pr-4">
        <div className="flex flex-wrap gap-1.5">
          {/* Retry button — available when row is in a retryable state */}
          {(row.status === 'failed' || (row.status === 'pending' && row.failure_reason)) && !row.on_hold && (
            <button onClick={doRetry} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px] !border-sky-300 !text-sky-700" data-testid={`retry-${row.commission_id}`}>
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          )}
          {/* Hold / Release toggle */}
          {!row.on_hold && (row.status === 'pending' || row.status === 'approved' || row.status === 'failed') && (
            <button onClick={doHold} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px]" data-testid={`hold-${row.commission_id}`}>
              Hold
            </button>
          )}
          {row.on_hold && (
            <button onClick={doRelease} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px] !border-amber-300 !text-amber-800" data-testid={`release-${row.commission_id}`}>
              Release
            </button>
          )}
          {row.status === 'pending' && !row.on_hold && (
            <button onClick={doApprove} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px]" data-testid={`approve-${row.commission_id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
            </button>
          )}
          {(row.status === 'pending' || row.status === 'approved') && !row.on_hold && (
            <button onClick={doMarkPaid} disabled={busy} className="ml-btn-gold !py-1 !px-2 text-[11px]" data-testid={`mark-paid-${row.commission_id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark Paid
            </button>
          )}
          {(row.status === 'pending' || row.status === 'approved') && (
            <button onClick={doCancel} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px] !border-red-300 !text-red-700" data-testid={`cancel-${row.commission_id}`}>
              <XCircle className="w-3.5 h-3.5" /> Cancel
            </button>
          )}
          {row.status === 'needs_admin_review' && (
            <>
              <button onClick={() => doResolveReview('reversed_manually')} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px]" data-testid={`resolve-reversed-${row.commission_id}`}>
                Reversed manually
              </button>
              <button onClick={() => doResolveReview('written_off')} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px]" data-testid={`resolve-writeoff-${row.commission_id}`}>
                Write off
              </button>
              <button onClick={() => doResolveReview('kept')} disabled={busy} className="ml-btn-outline !py-1 !px-2 text-[11px]" data-testid={`resolve-kept-${row.commission_id}`}>
                Keep payment
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
